/**
 * CAMASA CPS — Sub-painéis admin do Signature Book (dentro de ObraDetail).
 *
 * Componentes:
 *  - SBGaleriaAmbientePanel: galeria de fotos por ambiente
 *  - SBMateriaisAprovadosPanel: aprovação formal de materiais (lote, fornecedor, foto da chapa)
 *  - SBAceiteCamasaPanel: assinatura institucional CAMASA (admin-only)
 *  - SBTermoMedicaoPanel: placeholder do Termo de Medição Final (Etapa 6)
 */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  ImageIcon, Plus, Trash2, X, PenTool, CheckCircle2
} from "lucide-react";
import { API, fileUrl, fmt, MAT_CAT, AMBIENTES_SUGERIDOS } from "../lib";

/* ========== SB ENHANCEMENTS (admin, dentro da aba Signature Book) ========== */


export function SBGaleriaAmbientePanel({ obra, api, onReload }) {
  // Ambientes vindos das medições + custom + sugeridos. Lista única.
  const ambientesMedicoes = (obra.medicoes?.ambientes || []).map(a => a.nome).filter(Boolean);
  const fotosObra = (obra.fotos || []).filter(f => f.aprovada !== false && f.type !== "video");
  const ambientesUsados = [...new Set(fotosObra.map(f => f.ambiente).filter(Boolean))];
  const ambientesAll = [...new Set([...ambientesMedicoes, ...ambientesUsados])];
  const [novoAmbiente, setNovoAmbiente] = useState("");
  const [extras, setExtras] = useState([]); // ambientes vazios criados pelo admin (ainda sem foto)
  const lista = [...new Set([...ambientesAll, ...extras])];
  const fileRefs = useRef({});

  const fotosPor = (amb) => fotosObra.filter(f => (f.ambiente || "") === amb);

  const addAmbiente = () => {
    const nome = novoAmbiente.trim();
    if (!nome) return;
    if (lista.includes(nome)) { alert("Esse ambiente já existe."); return; }
    setExtras(prev => [...prev, nome]);
    setNovoAmbiente("");
  };

  const upload = async (amb, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Envie uma imagem (JPG/PNG/WEBP/HEIC)."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("ambiente", amb);
    fd.append("categoria", "obra");
    try {
      await axios.post(`${API}/obras/${obra.id}/upload`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem("cps_token")}` },
      });
      onReload && onReload();
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    }
  };

  const removeFoto = async (foto) => {
    if (!window.confirm("Remover esta foto?")) return;
    try {
      await api("delete", `/obras/${obra.id}/foto/${foto.id}`);
      onReload && onReload();
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };

  const setLegenda = async (foto) => {
    const cur = foto.legenda || "";
    const v = window.prompt("Legenda da foto:", cur);
    if (v === null) return;
    try {
      await api("patch", `/obras/${obra.id}/foto/${foto.id}`, { legenda: v });
      onReload && onReload();
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };

  const reatribuirAmbiente = async (foto, novoAmb) => {
    try {
      await api("patch", `/obras/${obra.id}/foto/${foto.id}`, { ambiente: novoAmb || null });
      onReload && onReload();
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };

  return (
    <div className="panel" style={{ marginTop: 20 }} data-testid="sb-galeria-ambiente">
      <div className="panel-eyebrow">Galeria por ambiente</div>
      <h3 className="panel-title" style={{ marginTop: 6 }}>Fotos da <em>obra concluída</em></h3>
      <p style={{ color: "var(--ink-2)", fontSize: 13, lineHeight: 1.7, marginTop: 10 }}>
        Suba fotos agrupadas por ambiente (Cozinha, Banheiro Master, Lavabo, Piso Externo etc.).
        No Signature Book do cliente, elas aparecem em <strong>grade elegante por grupo</strong>,
        substituindo a antiga foto única "A obra concluída".
      </p>

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
        <select className="form-input form-input-sm" style={{ maxWidth: 220 }} value="" onChange={e => { if (e.target.value) { setNovoAmbiente(e.target.value); } }} data-testid="sb-amb-sugest">
          <option value="">+ Ambiente sugerido…</option>
          {AMBIENTES_SUGERIDOS.filter(a => !lista.includes(a)).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <input className="form-input form-input-sm" placeholder="Ou digite um ambiente novo…" value={novoAmbiente} onChange={e => setNovoAmbiente(e.target.value)} style={{ maxWidth: 240 }} data-testid="sb-amb-novo" />
        <button className="btn btn-ghost btn-sm" onClick={addAmbiente} data-testid="sb-amb-add"><Plus size={12} /> Adicionar</button>
      </div>

      {lista.length === 0 ? (
        <p style={{ color: "var(--ink-3)", fontSize: 12, marginTop: 18, fontStyle: "italic" }}>
          Nenhum ambiente cadastrado ainda. Adicione um acima ou importe orçamento na aba <strong>Medições</strong>.
        </p>
      ) : (
        <div style={{ marginTop: 18 }}>
          {lista.map(amb => {
            const fotos = fotosPor(amb);
            return (
              <div key={amb} style={{ marginBottom: 22, padding: 14, border: "1px solid var(--line)", background: "var(--bg-3)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <ImageIcon size={14} color="var(--gold)" />
                    <strong style={{ color: "var(--gold)", fontSize: 14 }}>{amb}</strong>
                    <span style={{ fontSize: 11, color: "var(--ink-3)" }}>{fotos.length} foto(s)</span>
                  </div>
                  <div>
                    <input
                      ref={el => (fileRefs.current[amb] = el)}
                      type="file" accept="image/*" multiple style={{ display: "none" }}
                      onChange={async (e) => {
                        for (const f of Array.from(e.target.files || [])) {
                          await upload(amb, f);
                        }
                        if (fileRefs.current[amb]) fileRefs.current[amb].value = "";
                      }}
                      data-testid={`sb-amb-input-${amb}`}
                    />
                    <button className="btn btn-ghost btn-sm" onClick={() => fileRefs.current[amb]?.click()} data-testid={`sb-amb-upload-${amb}`}>
                      <Plus size={12} /> Adicionar fotos
                    </button>
                  </div>
                </div>
                {fotos.length === 0 ? (
                  <div style={{ padding: 16, textAlign: "center", color: "var(--ink-3)", fontSize: 12, fontStyle: "italic" }}>
                    Nenhuma foto neste ambiente ainda.
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
                    {fotos.map(f => (
                      <div key={f.id} style={{ position: "relative", border: "1px solid var(--line)", background: "var(--bg-2)" }}>
                        <div style={{ aspectRatio: "1/1", backgroundImage: `url(${fileUrl(f.url)})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                        {f.legenda && <div style={{ padding: "6px 8px", fontSize: 11, color: "var(--ink-2)", fontStyle: "italic", lineHeight: 1.4 }}>{f.legenda}</div>}
                        <div style={{ padding: 6, display: "flex", gap: 4, flexWrap: "wrap", borderTop: "1px solid var(--line)" }}>
                          <button className="chip" onClick={() => setLegenda(f)} title="Legenda" style={{ fontSize: 10 }}>✎</button>
                          <select className="chip" value={f.ambiente || ""} onChange={e => reatribuirAmbiente(f, e.target.value)} style={{ cursor: "pointer", fontSize: 10 }} title="Mover para outro ambiente">
                            {lista.map(a => <option key={a} value={a}>{a}</option>)}
                          </select>
                          <button className="chip" onClick={() => removeFoto(f)} style={{ color: "#c47a7a", fontSize: 10 }} title="Remover">×</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Fotos sem ambiente */}
      {fotosObra.filter(f => !f.ambiente).length > 0 && (
        <div style={{ marginTop: 14, padding: 12, border: "1px dashed var(--gold-3)", background: "rgba(184,147,90,0.04)" }}>
          <div style={{ fontSize: 12, color: "var(--ink-3)", marginBottom: 8 }}>
            <strong>{fotosObra.filter(f => !f.ambiente).length} foto(s)</strong> sem ambiente atribuído.
            Use a aba <strong>Mídias</strong> para gerenciá-las, ou edite uma a uma abaixo:
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
            {fotosObra.filter(f => !f.ambiente).slice(0, 12).map(f => (
              <div key={f.id} style={{ position: "relative" }}>
                <div style={{ aspectRatio: "1/1", backgroundImage: `url(${fileUrl(f.url)})`, backgroundSize: "cover", backgroundPosition: "center", border: "1px solid var(--line)" }} />
                <select className="chip" value="" onChange={e => e.target.value && reatribuirAmbiente(f, e.target.value)} style={{ width: "100%", marginTop: 4, fontSize: 10 }}>
                  <option value="">→ atribuir a…</option>
                  {lista.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function SBMateriaisAprovadosPanel({ obra, api, onReload }) {
  const [materiais, setMateriais] = useState([]);
  const aprov = obra.materiais_aprovacao || {};
  const fileRefs = useRef({});

  useEffect(() => {
    const ids = obra.materiais_ids || [];
    if (!ids.length) { setMateriais([]); return; }
    api("get", "/materiais").then(all => {
      setMateriais((all || []).filter(m => ids.includes(m.id)));
    }).catch(() => {});
  }, [api, obra.id, obra.materiais_ids]);

  const update = async (matId, body) => {
    try {
      await api("patch", `/obras/${obra.id}/material-aprovacao/${matId}`, body);
      onReload && onReload();
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };

  const uploadChapa = async (matId, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Envie uma imagem."); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/obras/${obra.id}/material-aprovacao/${matId}/foto`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem("cps_token")}` },
      });
      onReload && onReload();
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };

  const removeChapa = async (matId) => {
    if (!window.confirm("Remover foto da chapa?")) return;
    try {
      await api("delete", `/obras/${obra.id}/material-aprovacao/${matId}/foto`);
      onReload && onReload();
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };

  if (!materiais.length) {
    return (
      <div className="panel" style={{ marginTop: 20 }} data-testid="sb-materiais-aprovados">
        <div className="panel-eyebrow">Materiais aprovados</div>
        <h3 className="panel-title" style={{ marginTop: 6 }}>Aprovação <em>do cliente</em></h3>
        <p style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 10, fontStyle: "italic" }}>
          Vincule materiais a esta obra na aba <strong>Materiais</strong> para registrar aqui a aprovação formal do cliente.
        </p>
      </div>
    );
  }

  const totalAprov = materiais.filter(m => aprov[m.id]?.aprovado).length;

  return (
    <div className="panel" style={{ marginTop: 20 }} data-testid="sb-materiais-aprovados">
      <div className="panel-eyebrow">Materiais aprovados</div>
      <h3 className="panel-title" style={{ marginTop: 6 }}>Aprovação <em>formal do cliente</em></h3>
      <p style={{ color: "var(--ink-2)", fontSize: 13, lineHeight: 1.7, marginTop: 10 }}>
        Registre lote, fornecedor, data e (opcional) foto da chapa aprovada antes do início da produção.
        Aparece como <strong>seção formal</strong> no Signature Book do cliente.
      </p>
      <div style={{ fontSize: 12, color: "var(--gold)", marginTop: 6 }} data-testid="sb-mat-counter">
        {totalAprov}/{materiais.length} materiais aprovados
      </div>

      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 14 }}>
        {materiais.map(m => {
          const a = aprov[m.id] || {};
          const isAprov = !!a.aprovado;
          return (
            <div key={m.id} style={{ padding: 14, border: `1px solid ${isAprov ? "var(--gold)" : "var(--line)"}`, background: isAprov ? "rgba(184,147,90,0.05)" : "var(--bg-3)" }} data-testid={`sb-mat-row-${m.id}`}>
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ width: 80, height: 80, backgroundImage: m.imagens?.[0] ? `url(${fileUrl(m.imagens[0])})` : "none", backgroundColor: "var(--bg-3)", backgroundSize: "cover", backgroundPosition: "center", border: "1px solid var(--line)", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ fontFamily: "'Fraunces',serif", fontSize: 16, color: "var(--ink)", fontStyle: "italic" }}>{m.nome}</div>
                  <div style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".15em", textTransform: "uppercase", marginTop: 3 }}>{MAT_CAT[m.categoria] || m.categoria}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8, marginTop: 10 }}>
                    <input
                      className="form-input form-input-sm"
                      placeholder="Lote (ex: 4109)"
                      defaultValue={a.lote || ""}
                      onBlur={e => e.target.value !== (a.lote || "") && update(m.id, { lote: e.target.value })}
                      data-testid={`sb-mat-lote-${m.id}`}
                    />
                    <input
                      className="form-input form-input-sm"
                      placeholder="Fornecedor"
                      defaultValue={a.fornecedor || ""}
                      onBlur={e => e.target.value !== (a.fornecedor || "") && update(m.id, { fornecedor: e.target.value })}
                      data-testid={`sb-mat-forn-${m.id}`}
                    />
                    <input
                      type="date"
                      className="form-input form-input-sm"
                      defaultValue={a.data_aprovacao || ""}
                      onBlur={e => e.target.value !== (a.data_aprovacao || "") && update(m.id, { data_aprovacao: e.target.value })}
                      data-testid={`sb-mat-data-${m.id}`}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end", minWidth: 140 }}>
                  <button
                    className="btn btn-sm"
                    style={{ background: isAprov ? "var(--gold)" : "transparent", color: isAprov ? "#0a0a0a" : "var(--gold)", border: "1px solid var(--gold)", fontWeight: 600 }}
                    onClick={() => update(m.id, { aprovado: !isAprov })}
                    data-testid={`sb-mat-toggle-${m.id}`}
                  >
                    {isAprov ? "✓ Aprovado" : "Marcar aprovado"}
                  </button>
                  {a.foto_chapa ? (
                    <div style={{ position: "relative" }}>
                      <div style={{ width: 110, height: 70, backgroundImage: `url(${fileUrl(a.foto_chapa)})`, backgroundSize: "cover", backgroundPosition: "center", border: "1px solid var(--gold)" }} />
                      <button className="chip" onClick={() => removeChapa(m.id)} style={{ position: "absolute", top: -8, right: -8, background: "#0a0a0a", color: "#c47a7a", padding: "2px 6px", fontSize: 10 }}>×</button>
                    </div>
                  ) : (
                    <>
                      <input
                        ref={el => (fileRefs.current[m.id] = el)}
                        type="file" accept="image/*" style={{ display: "none" }}
                        onChange={e => uploadChapa(m.id, e.target.files?.[0])}
                      />
                      <button className="btn btn-ghost btn-sm" onClick={() => fileRefs.current[m.id]?.click()} data-testid={`sb-mat-foto-${m.id}`}>
                        <ImageIcon size={11} /> Foto da chapa
                      </button>
                    </>
                  )}
                </div>
              </div>
              {a.aprovado && a.aprovado_em && (
                <div style={{ fontSize: 11, color: "var(--verde-2)", marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--line)" }}>
                  ✓ Aprovado em {fmt(a.aprovado_em)} por {a.aprovado_por}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SBAceiteCamasaPanel({ obra, api, onReload }) {
  const ac = obra.aceite_camasa || null;
  const cliente = obra.aceite_cliente || null;

  const assinar = async () => {
    if (!window.confirm("Confirmar assinatura institucional CAMASA nesta obra?\n\nSerá registrado seu nome, e-mail, IP e timestamp como diretor responsável.")) return;
    try {
      await api("post", `/obras/${obra.id}/aceite-camasa`, {});
      onReload && onReload();
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };

  const remover = async () => {
    if (!window.confirm("Remover assinatura CAMASA? (Pode ser refeita depois.)")) return;
    try {
      await api("delete", `/obras/${obra.id}/aceite-camasa`);
      onReload && onReload();
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };

  const fullySigned = !!ac && !!cliente;

  return (
    <div className="panel" style={{ marginTop: 20 }} data-testid="sb-aceite-camasa">
      <div className="panel-eyebrow" style={{ color: fullySigned ? "var(--verde-2)" : "var(--gold)" }}>
        {fullySigned ? "Entregue e Assinada" : "Assinatura institucional CAMASA"}
      </div>
      <h3 className="panel-title" style={{ marginTop: 6 }}>
        {fullySigned ? "Obra " : "Assinatura "}<em>{fullySigned ? "concluída" : "da CAMASA"}</em>
      </h3>

      {/* Status duplo */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginTop: 16 }}>
        <div style={{ padding: 14, border: `1px solid ${ac ? "var(--gold)" : "var(--line)"}`, background: ac ? "rgba(184,147,90,0.06)" : "var(--bg-3)" }}>
          <div style={{ fontSize: 10, letterSpacing: ".25em", color: ac ? "var(--gold)" : "var(--ink-3)", textTransform: "uppercase" }}>CAMASA</div>
          {ac ? (
            <>
              <div style={{ marginTop: 8, fontFamily: "'Fraunces',serif", fontSize: 17, fontStyle: "italic", color: "var(--ink)" }}>{ac.nome}</div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>{ac.cargo}</div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 8 }}>
                {fmt(ac.data)} · {new Date(ac.data).toLocaleTimeString("pt-BR")} · IP {ac.ip || "—"}
              </div>
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 10, color: "#c47a7a", borderColor: "#4a2a2a" }} onClick={remover} data-testid="sb-camasa-remove">
                Remover
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 8, lineHeight: 1.6 }}>
                Aguardando assinatura institucional do diretor CAMASA.
              </p>
              <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={assinar} data-testid="sb-camasa-sign">
                <PenTool size={11} /> Assinar como CAMASA
              </button>
            </>
          )}
        </div>
        <div style={{ padding: 14, border: `1px solid ${cliente ? "var(--verde-2)" : "var(--line)"}`, background: cliente ? "rgba(90,122,106,0.08)" : "var(--bg-3)" }}>
          <div style={{ fontSize: 10, letterSpacing: ".25em", color: cliente ? "var(--verde-2)" : "var(--ink-3)", textTransform: "uppercase" }}>Cliente</div>
          {cliente ? (
            <>
              <div style={{ marginTop: 8, fontFamily: "'Fraunces',serif", fontSize: 17, fontStyle: "italic", color: "var(--ink)" }}>{cliente.nome}</div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>{cliente.tipo || "cliente"}</div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 8 }}>
                {fmt(cliente.data)} · IP {cliente.ip || "—"}
              </div>
            </>
          ) : (
            <p style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 8, lineHeight: 1.6 }}>
              Cliente registra na página pública do Signature Book.
            </p>
          )}
        </div>
      </div>

      {fullySigned && (
        <div style={{ marginTop: 16, padding: 12, background: "rgba(90,122,106,0.1)", border: "1px solid var(--verde-2)", textAlign: "center" }}>
          <CheckCircle2 size={18} color="var(--verde-2)" style={{ display: "inline", verticalAlign: "middle", marginRight: 8 }} />
          <strong style={{ color: "var(--verde-2)" }}>Obra Entregue e Assinada</strong>
          <span style={{ fontSize: 12, color: "var(--ink-2)", marginLeft: 8 }}>— ambas as partes confirmaram.</span>
        </div>
      )}
    </div>
  );
}

export function SBTermoMedicaoPanel({ obra, api }) {
  const med = obra.medicoes;
  const ambFechados = (med?.ambientes || []).filter(a => a.fechado).length;
  const totalAmb = (med?.ambientes || []).length;
  const termo = med?.termo_final;
  const aceite = med?.termo_aceite_cliente;

  return (
    <div className="panel" style={{ marginTop: 20 }} data-testid="sb-termo-medicao">
      <div className="panel-eyebrow">Termo de Medição Final</div>
      <h3 className="panel-title" style={{ marginTop: 6 }}>Documento <em>de medição</em></h3>

      {!med ? (
        <p style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 10, fontStyle: "italic" }}>
          Importe o orçamento na aba <strong>Medições</strong> para habilitar o Termo de Medição Final.
        </p>
      ) : termo ? (
        <div style={{ marginTop: 14, padding: 14, border: "1px solid var(--gold-3)", background: "rgba(184,147,90,0.05)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <CheckCircle2 size={18} color="var(--gold)" />
            <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 16, color: "var(--ink)" }}>
              Termo gerado · v{termo.version}
            </div>
          </div>
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginBottom: 12 }}>
            {fmt(termo.gerado_em)} por {termo.gerado_por} · {Math.round((termo.size_bytes || 0) / 1024)}KB
          </div>
          <a href={fileUrl(termo.url)} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" data-testid="sb-termo-view-btn">
            Visualizar PDF
          </a>
          {aceite ? (
            <div style={{ marginTop: 12, fontSize: 12, color: "var(--verde-2)" }}>
              ✓ Aceito pelo cliente em {fmt(aceite.data)} ({aceite.nome})
            </div>
          ) : (
            <div style={{ marginTop: 12, fontSize: 11, color: "var(--ink-3)", fontStyle: "italic" }}>
              Aguardando aceite do cliente — ele recebe o link na aba Signature Book pública.
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 14, padding: 14, border: "1px dashed var(--gold-3)", background: "rgba(184,147,90,0.04)" }}>
          <p style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.7, margin: 0 }}>
            <strong>{ambFechados}/{totalAmb}</strong> ambientes fechados.
            Gere o Termo Final na aba <strong>Medições</strong> (botão "Gerar Termo Final" no topo).
          </p>
          <p style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 8, fontStyle: "italic" }}>
            Quando gerado, aparecerá aqui com link de visualização e área de aceite separada para o cliente.
          </p>
        </div>
      )}
    </div>
  );
}

